import { Fragment } from "react";
import {
  Box,
  Table,
  Tag,
  Text,
} from "@chakra-ui/react";

import ServiceSessionTimeGraphs from "../charts/ServiceSessionTimeGraphs";

import { useSelector } from "react-redux";
import { formatTime } from "../../../store/helpers";
import {
  AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  DEFAULT_RENDER_CONFIG,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import {
  resolveAgentServiceServiceTicketCount,
  resolveAgentServiceTicketCount,
} from "../../../store/agentServiceTimeMetrics";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";

const getVisibleColumns = (renderConfig) => {
  const config = normalizeRenderConfig(renderConfig);
  return AGENT_SERVICE_TIME_COLUMN_DEFINITIONS.filter((column) => config.columns.includes(column.id));
};

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatNumber = (value, digits = 2) => Number(value || 0).toFixed(digits);

const getServiceTimeShare = (agentData) => agentData.serviceTimeShare ?? agentData.totalPct ?? 0;

const renderCell = (agentData, columnId) => {
  if (columnId === "serviceName") return agentData.serviceName;
  if (columnId === "agentName") return agentData.agentName;
  if (columnId === "ticketCount") return resolveAgentServiceTicketCount(agentData);
  if (columnId === "totalServiceTime") {
    return <Text color={"var(--green)"}>{formatTime(agentData.sum)}</Text>;
  }
  if (columnId === "percentage" || columnId === "serviceTimeShare") {
    return formatPercent(getServiceTimeShare(agentData));
  }
  if (columnId === "average") return formatTime(agentData.avgPerTicket ?? agentData.average);
  if (columnId === "serviceAverage") return formatTime(agentData.serviceAverage);
  if (columnId === "avgVsServiceAveragePct") {
    return formatPercent(agentData.avgVsServiceAveragePct);
  }
  if (columnId === "median") return formatTime(agentData.medianServiceTime ?? agentData.median);
  if (columnId === "p90") return formatTime(agentData.p90ServiceTime ?? agentData.p90);
  if (columnId === "activeDays") return agentData.activeDays ?? 0;
  if (columnId === "ticketsPerActiveDay") return formatNumber(agentData.ticketsPerActiveDay);
  if (columnId === "flags") {
    return Array.isArray(agentData.flags) && agentData.flags.length
      ? agentData.flags.join(", ")
      : agentData.effectivenessBand || "";
  }
  if (columnId === "min") return formatTime(agentData.min);
  if (columnId === "max") return formatTime(agentData.max);
  return null;
};

const ServiceSummaryTable = ({ serviceData }) => {
  const summaryRows = [
    ["Completed Tickets", resolveAgentServiceServiceTicketCount(serviceData)],
    ["Total Service Time", formatTime(serviceData.totalServiceTime)],
    ["Avg / Ticket", formatTime(serviceData.averageServiceTime ?? serviceData.average ?? 0)],
  ];

  if (serviceData.unassignedCompletedTickets) {
    summaryRows.push(
      ["Unassigned Tickets", serviceData.unassignedCompletedTickets],
      ["Unassigned Service Time", formatTime(serviceData.unassignedServiceTime)]
    );
  }

  return (
    <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
      <Table.Body>
        {summaryRows.map(([label, value]) => (
          <Table.Row key={label}>
            <Table.Cell>{label}</Table.Cell>
            <Table.Cell>{value}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
};

const isLegacyChartConfig = (config) =>
  config.charts.enabled &&
  config.density === DEFAULT_RENDER_CONFIG.density &&
  !config.sort &&
  config.columns.length === DEFAULT_RENDER_CONFIG.columns.length &&
  config.columns.every((column, index) => column === DEFAULT_RENDER_CONFIG.columns[index]);

const AgentServicesTableRows = ({ data, renderConfig }) => {
  const config = normalizeRenderConfig(renderConfig);
  const visibleColumns = getVisibleColumns(config);
  const rows = sortReportRows(data.filter((agentData) => agentData.sum), config);
  const tableClassName = `stripedTable reportDataTable reportDataTable--${config.density}`;
  const useLegacyChartTable = isLegacyChartConfig(config);

  if (!config.charts.enabled) {
    return (
      <ConfiguredReportTable
        columns={AGENT_SERVICE_TIME_COLUMN_DEFINITIONS}
        rows={rows}
        renderConfig={config}
        renderCell={renderCell}
        presortedRows={true}
      />
    );
  }

  return (
    <>
      {rows.map((agentData, index) => {
        const { agentName, serviceName } = agentData;
        return agentData.sum ? (
          <Fragment key={index}>
            <Box className="reportContainer" mt="20px" mb="20px">
              <Table.Root className={tableClassName} size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>#</Table.ColumnHeader>
                    {useLegacyChartTable ? (
                      <>
                        <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                        <Table.ColumnHeader>Completed Tickets</Table.ColumnHeader>
                        <Table.ColumnHeader>Total Service Time</Table.ColumnHeader>
                        <Table.ColumnHeader>Share of Service Time</Table.ColumnHeader>
                        <Table.ColumnHeader>Avg / Ticket</Table.ColumnHeader>
                        <Table.ColumnHeader>Min Ticket Time</Table.ColumnHeader>
                        <Table.ColumnHeader>Max Ticket Time</Table.ColumnHeader>
                      </>
                    ) : (
                      visibleColumns.map((column) => (
                        <Table.ColumnHeader key={column.id}>{column.label}</Table.ColumnHeader>
                      ))
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>{index + 1}</Table.Cell>
                    {useLegacyChartTable ? (
                      <>
                        <Table.Cell>{agentData.agentName}</Table.Cell>
                        <Table.Cell>{resolveAgentServiceTicketCount(agentData)}</Table.Cell>
                        <Table.Cell>
                          <Text color={"var(--green)"}>{formatTime(agentData.sum)}</Text>
                        </Table.Cell>
                        <Table.Cell>{formatPercent(getServiceTimeShare(agentData))}</Table.Cell>
                        <Table.Cell>{formatTime(agentData.avgPerTicket ?? agentData.average)}</Table.Cell>
                        <Table.Cell>{formatTime(agentData.min)}</Table.Cell>
                        <Table.Cell>{formatTime(agentData.max)}</Table.Cell>
                      </>
                    ) : (
                      visibleColumns.map((column) => (
                        <Table.Cell key={column.id}>{renderCell(agentData, column.id)}</Table.Cell>
                      ))
                    )}
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={useLegacyChartTable ? 8 : visibleColumns.length + 1}>
                      <Box mt="20px" mb="20px">
                        <ServiceSessionTimeGraphs
                          title={`${agentName}: ${serviceName}, Day by Day Comparison`}
                          chart={agentData.chart}
                          height={"200px"}
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>
          </Fragment>
        ) : (
          <Fragment key={index}>
            <Text fontWeight={500}>
              No data for agent: {agentData?.agentName ?? "Unknown"}
            </Text>
          </Fragment>
        );
      })}
    </>
  );
};

const ServicesSessionTimeTable = ({
  serviceData,

  branchName,
  reportIndex,
  branchIndex,
  index,
}) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const config = normalizeRenderConfig(renderConfig);
  const { totalServiceTime, agentsData, serviceName } = serviceData;
  const completedTickets = resolveAgentServiceServiceTicketCount(serviceData);
  const title = `${reportIndex || ""}.${branchIndex + 1}.${index + 1}. ${serviceName}`;
  const blockClassName = `agentServiceTimeServiceBlock${
    !config.charts.enabled ? " agentServiceTimeServiceBlock--tableOnly" : ""
  }`;

  return (
    <Box className={blockClassName}>
      <h3>{title}</h3>
      {!totalServiceTime && (
        <Text fontSize="sm">No data available for this report</Text>
      )}
      <Tag.Root>
        <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
      </Tag.Root>
      {totalServiceTime ? (
        <Text fontSize="sm" color="gray.600" mt="6px" mb={!config.charts.enabled ? "12px" : "0"}>
          {completedTickets ?? "Unknown"} completed tickets · {formatTime(totalServiceTime)} total service time
        </Text>
      ) : null}
      {totalServiceTime ? (
        <Box mt="10px" mb="12px">
          <ServiceSummaryTable serviceData={serviceData} />
        </Box>
      ) : null}
      {totalServiceTime ? (
        <>
          <AgentServicesTableRows data={agentsData} renderConfig={renderConfig} />
        </>
      ) : <></>}
    </Box>
  );
};

export default ServicesSessionTimeTable;
